import { TFunction } from "i18next";
import { BoundedNameValidationError } from "../../enums/BoundedNameValidationError";
import { KeycloakEmailValidationError } from "../../enums/KeycloakEmailValidationError";
import { BoundedName } from "@pulse/pulse-rpcs/lib/types/boundedName";

export interface EmailCycleBasicInformationValidationError {
  cycleName: BoundedNameValidationError | undefined;
  fromEmailId: KeycloakEmailValidationError | undefined;
  displayName: BoundedNameValidationError | undefined;
  subject: BoundedNameValidationError | undefined;
}

export const getEmailCycleNameValidationErrorMessage = (
  t: TFunction,
  validationError: BoundedNameValidationError,
): string => {
  switch (validationError) {
    case BoundedNameValidationError.MinSize:
      return t("emailCycles.emailCycleNameMinSizeErrorMessage");
    case BoundedNameValidationError.MaxSize:
      return t("emailCycles.emailCycleNameMaxSizeErrorMessages", {
        maxSize: BoundedName.constraints.name.maxSize,
      });
    case BoundedNameValidationError.DuplicateName:
      return t("emailCycles.duplicateEmailCycleNameErrorMessages");
    default:
      return validationError satisfies never;
  }
};

export const getFromEmailIdValidationErrorMessage = (
  t: TFunction,
  validationError: KeycloakEmailValidationError,
): string => {
  switch (validationError) {
    case KeycloakEmailValidationError.EmptyValue:
      return t("emailCycles.fromEmailIdEmptyValueErrorMessage");
    case KeycloakEmailValidationError.InvalidEmailId:
      return t("emailCycles.invalidFromEmailIdErrorMessage");
    default:
      return validationError satisfies never;
  }
};

export const getDisplayNameValidationErrorMessage = (
  t: TFunction,
  validationError: BoundedNameValidationError,
): string => {
  switch (validationError) {
    case BoundedNameValidationError.MinSize:
      return t("emailCycles.displayNameMinSizeErrorMessage");
    case BoundedNameValidationError.MaxSize:
      return t("emailCycles.displayNameMaxSizeErrorMessages", {
        maxSize: BoundedName.constraints.name.maxSize,
      });
    case BoundedNameValidationError.DuplicateName:
      throw new Error(
        "Display name should not throw DuplicateName validation error",
      );
    default:
      return validationError satisfies never;
  }
};

export const getSubjectValidationErrorMessage = (
  t: TFunction,
  validationError: BoundedNameValidationError,
): string => {
  switch (validationError) {
    case BoundedNameValidationError.MinSize:
      return t("emailCycles.subjectMinSizeErrorMessage");
    case BoundedNameValidationError.MaxSize:
      return t("emailCycles.subjectMaxSizeErrorMessages", {
        maxSize: BoundedName.constraints.name.maxSize,
      });
    case BoundedNameValidationError.DuplicateName:
      throw new Error(
        "Subject should not throw DuplicateName validation error",
      );
    default:
      return validationError satisfies never;
  }
};

export const getEmailCycleBasicInformationValidationErrorMessage = (
  t: TFunction,
  validationErrors: EmailCycleBasicInformationValidationError,
): Record<
  keyof EmailCycleBasicInformationValidationError,
  string | undefined
> => ({
  cycleName: validationErrors.cycleName
    ? getEmailCycleNameValidationErrorMessage(t, validationErrors.cycleName)
    : undefined,
  fromEmailId: validationErrors.fromEmailId
    ? getFromEmailIdValidationErrorMessage(t, validationErrors.fromEmailId)
    : undefined,
  displayName: validationErrors.displayName
    ? getDisplayNameValidationErrorMessage(t, validationErrors.displayName)
    : undefined,
  subject: validationErrors.subject
    ? getSubjectValidationErrorMessage(t, validationErrors.subject)
    : undefined,
});
